// @ts-check

/** @file Parses and formats user-entered durations. */

import gettextConfig from "gettext-universal/build/src/config.js"

const DURATION_TOKEN_PATTERN = /(\d+(?:[.,]\d+)?)\s*([a-zæøå.]+)/giu
const DEFAULT_DURATION_LOCALE = "en"
/** @typedef {{hours: string, minutes: string, seconds: string}} DurationUnitLabels */
/** @type {Record<string, DurationUnitLabels>} */
const DURATION_UNIT_LABELS_BY_LOCALE = {
  da: {hours: "t", minutes: "m", seconds: "s"},
  de: {hours: "h", minutes: "m", seconds: "s"},
  en: {hours: "h", minutes: "m", seconds: "s"}
}
const HOUR_UNITS = new Set([
  "h",
  "hour",
  "hours",
  "hr",
  "hrs",
  "std",
  "std.",
  "stunde",
  "stunden",
  "t",
  "time",
  "timer"
])
const MINUTE_UNITS = new Set([
  "m",
  "min",
  "min.",
  "mins",
  "minute",
  "minutes",
  "minut",
  "minutter"
])
const SECOND_UNITS = new Set([
  "s",
  "sec",
  "sec.",
  "secs",
  "second",
  "seconds",
  "sek",
  "sek.",
  "sekund",
  "sekunder",
  "sekunde",
  "sekunden"
])

/**
 * Formats seconds as compact unit-suffixed input text.
 * @param {number | null | undefined} seconds Stored duration value.
 * @param {string | null | undefined} [locale] Locale used for unit labels.
 * @returns {string} Duration text, or blank when no duration exists.
 */
export function durationSecondsToInput(seconds, locale = gettextConfig.getLocale() || DEFAULT_DURATION_LOCALE) {
  const parsedSeconds = Number(seconds || 0)
  if (!Number.isFinite(parsedSeconds) || parsedSeconds <= 0) return ""

  const wholeSeconds = Math.round(parsedSeconds)
  if (wholeSeconds <= 0) return ""

  const hours = Math.floor(wholeSeconds / 3600)
  const minutes = Math.floor((wholeSeconds % 3600) / 60)
  const remainingSeconds = wholeSeconds % 60
  const parts = []
  const labels = durationUnitLabels(locale)

  if (hours > 0) parts.push(`${hours}${labels.hours}`)
  if (minutes > 0) parts.push(`${minutes}${labels.minutes}`)
  if (remainingSeconds > 0) parts.push(`${remainingSeconds}${labels.seconds}`)

  return parts.join(" ")
}

/**
 * Parses a duration string into whole seconds.
 * @param {string} value User-entered duration text.
 * @returns {number | null} Whole seconds, or null when the format is not recognized.
 */
export function parseDurationSeconds(value) {
  const trimmed = value.trim()
  if (!trimmed) return null

  const decimalHours = parseDecimalHours(trimmed)
  if (decimalHours != null) return decimalHours

  const clockDuration = parseClockDuration(trimmed)
  if (clockDuration != null) return clockDuration

  return parseDurationTokens(trimmed)
}

/**
 * Parses a locale-tolerant decimal hour value.
 * @param {string} value Duration input.
 * @returns {number | null} Whole seconds, or null when the value is not decimal hours.
 */
function parseDecimalHours(value) {
  const normalized = value.replace(",", ".")
  if (!/^\d+(?:\.\d+)?$/.test(normalized)) return null

  const hours = Number(normalized)
  if (!Number.isFinite(hours)) return null

  return Math.round(hours * 3600)
}

/**
 * Parses `hours:minutes` duration text.
 * @param {string} value Duration input.
 * @returns {number | null} Whole seconds, or null when the value is not clock-style.
 */
function parseClockDuration(value) {
  const match = value.match(/^(\d+):([0-5]?\d)$/)
  if (!match) return null

  return (Number(match[1]) * 3600) + (Number(match[2]) * 60)
}

/**
 * Parses unit-suffixed duration tokens such as `1h 5m 30s`.
 * @param {string} value Duration input.
 * @returns {number | null} Whole seconds, or null when tokens do not cover the input.
 */
function parseDurationTokens(value) {
  DURATION_TOKEN_PATTERN.lastIndex = 0
  let cursor = 0
  let matched = false
  let seconds = 0
  let match = DURATION_TOKEN_PATTERN.exec(value)

  while (match) {
    if (!durationTokenSeparatorIsValid(value.slice(cursor, match.index))) return null

    const amount = Number(match[1].replace(",", "."))
    const multiplier = secondsMultiplierForUnit(match[2].toLowerCase())

    if (!Number.isFinite(amount) || multiplier == null) return null

    seconds += amount * multiplier
    matched = true
    cursor = DURATION_TOKEN_PATTERN.lastIndex
    match = DURATION_TOKEN_PATTERN.exec(value)
  }

  if (!matched || value.slice(cursor).trim() !== "") return null

  return Math.round(seconds)
}

/**
 * Checks that text between duration tokens contains only supported separators.
 * @param {string} value Text between parsed duration tokens.
 * @returns {boolean} Whether the separator is valid.
 */
function durationTokenSeparatorIsValid(value) {
  return /^[,\s]*$/.test(value)
}

/**
 * Maps a duration unit token to its second multiplier.
 * @param {string} unit Unit suffix.
 * @returns {number | null} Unit multiplier, or null when unsupported.
 */
function secondsMultiplierForUnit(unit) {
  if (HOUR_UNITS.has(unit)) return 3600
  if (MINUTE_UNITS.has(unit)) return 60
  if (SECOND_UNITS.has(unit)) return 1
  return null
}

/**
 * Finds compact duration unit labels for a locale.
 * @param {string | null | undefined} locale Locale identifier.
 * @returns {DurationUnitLabels} Suffixes used when formatting duration input text.
 */
function durationUnitLabels(locale) {
  if (!locale) return DURATION_UNIT_LABELS_BY_LOCALE[DEFAULT_DURATION_LOCALE]

  const localeCode = locale.slice(0, 2).toLowerCase()

  return DURATION_UNIT_LABELS_BY_LOCALE[localeCode] || DURATION_UNIT_LABELS_BY_LOCALE[DEFAULT_DURATION_LOCALE]
}
